import React, { useState, useEffect, useRef } from 'react';
import { Check, X, Mic, MicOff } from 'lucide-react';

// ── Modal ──────────────────────────────────────────────
export function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: wide ? 800 : 600 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>{title}</h2>
          <button className="btn btn-icon btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Checkbox ─────────────────────────────────────────
export function Checkbox({ checked, onChange }) {
  return (
    <div className={`checkbox ${checked ? 'checked' : ''}`} onClick={onChange}>
      {checked && <Check size={10} color="#0E0E0C" strokeWidth={3} />}
    </div>
  );
}

// ── Badge ────────────────────────────────────────────
export function StatusBadge({ status }) {
  const key = status?.replace(/\s+/g, '');
  return <span className={`badge badge-status-${key}`}>{status}</span>;
}
export function ImportanceBadge({ value }) {
  return <span className={`badge badge-imp-${value}`}>{value}</span>;
}
export function UrgencyBadge({ value }) {
  const key = value?.replace(/\s+/g, '.');
  return <span className={`badge badge-urg-${key}`}>{value}</span>;
}

// ── Toast ─────────────────────────────────────────────
let toastFn = null;
export function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    toastFn = (msg, type = 'info') => {
      const id = Date.now();
      setToasts(t => [...t, { id, msg, type }]);
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
    };
  }, []);
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 9999 }}>
      {toasts.map(t => (
        <div key={t.id} className="toast" style={{ borderLeft: `3px solid ${t.type === 'error' ? 'var(--red)' : t.type === 'success' ? 'var(--green)' : 'var(--accent)'}` }}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}
export const toast = (msg, type) => toastFn?.(msg, type);

// ── Confirm Dialog ────────────────────────────────────
export function Confirm({ message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 380 }}>
        <p style={{ marginBottom: 20, color: 'var(--text)' }}>{message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onCancel}>Abbrechen</button>
          <button className="btn btn-danger" onClick={onConfirm}>Löschen</button>
        </div>
      </div>
    </div>
  );
}

// ── Progress Bar ──────────────────────────────────────
export function ProgressBar({ value, max = 100, color }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div style={{ background: 'var(--bg-3)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color || 'var(--accent)', borderRadius: 4, transition: 'width 0.3s' }} />
    </div>
  );
}

// ── Speech Recognition ────────────────────────────────
const SpeechRec = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

export function MicButton({ onTranscript, lang = 'de-DE', size = 14 }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  useEffect(() => () => recRef.current?.stop(), []);

  if (!SpeechRec) return null;

  const start = () => {
    const rec = new SpeechRec();
    rec.lang = lang;
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const text = Array.from(e.results).map(r => r[0].transcript).join(' ').trim();
      if (text) onTranscript(text);
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      if (e.error !== 'aborted' && e.error !== 'no-speech') toast(`Spracheingabe: ${e.error}`, 'error');
    };
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const stop = () => recRef.current?.stop();

  return (
    <button
      type="button"
      className="btn btn-icon btn-ghost btn-sm"
      onClick={listening ? stop : start}
      title={listening ? 'Stoppen' : 'Spracheingabe'}
      style={{ color: listening ? 'var(--red)' : undefined, flexShrink: 0 }}
    >
      {listening ? <MicOff size={size} /> : <Mic size={size} />}
    </button>
  );
}

// ── Empty State ───────────────────────────────────────
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 12, color: 'var(--text-3)' }}>
      {icon && <div style={{ fontSize: 40, marginBottom: 8 }}>{icon}</div>}
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, color: 'var(--text-2)' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13 }}>{subtitle}</div>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}
